import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * Makes the products in EasySell's order form editable: a quantity stepper and a remove button on every
 * line, plus a button that closes the form so the buyer can add another product.
 *
 * The app renders the form with its own Vue app and mirrors the storefront cart in a Vuex store, with no
 * public API for changing a line. The editor therefore changes the cart through the Ajax API and asks the
 * app's store to reload it, repeating the follow-up the app runs after its own remove button so shipping,
 * add-ons and discounts are recalculated. If that store can't be found, nothing is added and the app's
 * own list stays as it is. The same refresh runs when the form opens or the storefront cart changes so
 * asynchronously added Bundlex lines and their discounts reach EasySell before the order is submitted.
 * Markup and labels come from snippets/easysell-cart-editor.liquid.
 */

const LINE_SELECTOR = '#easysell .es-line-item';
const EDITOR_SELECTOR = ':scope > .es-line-editor';
const EASYSELL_BUTTON_SELECTOR = '#easysell .es-button';
const EASYSELL_POPUP_BUTTON_SELECTOR = '#es-popup-button.es-popup-button-product';
const BUNDLEX_WIDGET_SELECTOR = '.bx-widget-container[data-bx-discount-id]';
const QUANTITY_DEBOUNCE = 300;
const BUNDLEX_CART_TIMEOUT = 10000;

/**
 * @typedef {object} LineEdit
 * @property {{ id: string } | { line: number }} target - How the Ajax API finds the line.
 * @property {number} variantId
 * @property {number} quantity - The quantity the buyer wants; 0 removes the line.
 * @property {boolean} sent
 */

/**
 * @typedef {object} BundlexSelection
 * @property {string} discountId
 * @property {string} offerKey
 * @property {number} itemCount
 */

/** @type {Map<number, LineEdit>} Edits the app's store doesn't show yet, keyed by line index. */
const edits = new Map();

/** @type {{ index: number, message: string } | null} */
let failure = null;

let flushing = false;

/** Set while a line is being removed, because the lines after it are about to shift. */
let locked = false;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let debounce;

let frame = 0;

/** @type {HTMLElement | null} */
let observedForm = null;

/** @type {HTMLButtonElement | null} A form button pressed while edits were still on their way to the cart. */
let heldButton = null;

/** @type {Promise<void> | null} */
let formSync = null;

/** A cart update received during a sync needs one final pass over the newest cart. */
let formSyncQueued = false;

/** Prevents repeated EasySell clicks while Bundlex is still adding the selected offer. */
let bundlexHandoffPending = false;

/** @returns {EasySellStore | null} */
function getStore() {
  const store = window.easysellApp?.$children?.[0]?.$store;
  if (!store || !Array.isArray(store.state?.cart?.cart?.items)) return null;

  // Without the reload action an edit could never reach the form, so the app's list is left alone.
  return store._actions && 'cart/fetchCart' in store._actions ? store : null;
}

/** @returns {HTMLElement[]} */
function getRows() {
  return /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll(LINE_SELECTOR)));
}

/** @returns {HTMLElement | null} The form's own close button. Embedded forms have none. */
function getCloseControl() {
  const control = document.querySelector('#easysell .es-form-header .close-icon');
  return control instanceof HTMLElement ? control : null;
}

/**
 * Finds a selected Bundlex offer that EasySell cannot add by itself. EasySell's product-page button
 * updates one variant directly; Bundlex needs the native product form to preserve companion lines and
 * its private discount properties.
 * @param {Element} target
 * @returns {{ popupButton: HTMLElement, productForm: HTMLElement,
 *   addControl: HTMLButtonElement | HTMLInputElement, activeOffer: HTMLElement,
 *   selection: BundlexSelection } | null}
 */
function getBundlexHandoff(target) {
  const popupButton = target.closest(EASYSELL_POPUP_BUTTON_SELECTOR);
  if (!(popupButton instanceof HTMLElement) || typeof window.openTysloForm !== 'function') return null;

  const form = popupButton.closest('form[data-type="add-to-cart-form"]');
  const productForm = form?.closest('product-form-component');
  const widget = productForm?.querySelector(BUNDLEX_WIDGET_SELECTOR);
  const activeOffer = widget?.querySelector('.js-bx-offer.bx-active[data-offer-key]');
  const addControl = form?.querySelector(
    'button[name="add"], button[type="submit"], input[name="add"], input[type="submit"]'
  );

  if (
    !(productForm instanceof HTMLElement) ||
    !(activeOffer instanceof HTMLElement) ||
    (!(addControl instanceof HTMLButtonElement) && !(addControl instanceof HTMLInputElement)) ||
    addControl.disabled
  ) {
    return null;
  }

  // Offer zero is Bundlex's plain-product baseline unless it contains an actual bundle composition.
  const requiresBundlex = activeOffer.dataset.offerKey !== '0' || activeOffer.querySelector('.bx-bundle-products');
  const discountId = widget instanceof HTMLElement ? widget.dataset.bxDiscountId : undefined;
  const offerKey = activeOffer.dataset.offerKey;
  const itemCount = Math.max(1, activeOffer.querySelectorAll('.bx-bundle-products .bx-cross-sell-item').length);

  return requiresBundlex && discountId && offerKey !== undefined
    ? { popupButton, productForm, addControl, activeOffer, selection: { discountId, offerKey, itemCount } }
    : null;
}

/**
 * @param {{ properties?: Record<string, unknown>, key?: string }} item
 * @returns {{ d?: unknown, o?: unknown, b?: unknown } | null}
 */
function getBundlexProperties(item) {
  const value = item.properties?._bundlex;
  if (typeof value !== 'string') return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * @param {{ items?: Array<{ properties?: Record<string, unknown>, key?: string }> }} cart
 * @param {BundlexSelection} selection
 */
function cartHasBundlexSelection(cart, selection) {
  /** @type {Map<string, number>} */
  const groups = new Map();

  for (const item of cart.items ?? []) {
    const properties = getBundlexProperties(item);
    if (!properties) continue;
    if (String(properties.d) !== selection.discountId || String(properties.o) !== selection.offerKey) continue;

    const group = typeof properties.b === 'string' ? properties.b : item.key;
    if (!group) continue;
    groups.set(group, (groups.get(group) ?? 0) + 1);
  }

  return Array.from(groups.values()).some((count) => count >= selection.itemCount);
}

/** @returns {Promise<{ items?: Array<{ properties?: Record<string, unknown>, key?: string }> }>} */
async function fetchCartSnapshot() {
  const response = await fetch(`${Theme.routes.cart_url ?? '/cart'}.json`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/**
 * Waits for Bundlex's cart completion event. Some Bundlex integrations own the native request completely,
 * so a small bounded cart check is the fallback when no theme event is emitted.
 * @param {HTMLElement} productForm
 * @param {BundlexSelection} selection
 * @returns {Promise<void>}
 */
function waitForBundlexCartAdd(productForm, selection) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = setTimeout(() => finish(new Error('Bundlex cart add timed out')), BUNDLEX_CART_TIMEOUT);

    /** @param {Error} [error] */
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      productForm.removeEventListener(StandardEvents.cartLinesUpdate, handleCartUpdate);
      productForm.removeEventListener(StandardEvents.cartError, handleCartError);
      document.removeEventListener('cart:updated', handleBundlexCartUpdate);
      document.removeEventListener('cart:refresh', handleBundlexCartUpdate);
      error ? reject(error) : resolve();
    };

    /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
    const handleCartUpdate = (event) => {
      if (!event.promise) {
        finish();
        return;
      }

      event.promise
        .then(({ detail }) => finish(detail?.didError ? new Error('Bundlex cart add failed') : undefined))
        .catch((error) => finish(error));
    };

    /** @param {Event} event */
    const handleCartError = (event) => {
      const message = 'detail' in event && event.detail?.error ? String(event.detail.error) : 'Cart add failed';
      finish(new Error(message));
    };

    // Bundlex can own the request completely, bypassing the theme's standardized product-form event.
    const handleBundlexCartUpdate = () => finish();

    productForm.addEventListener(StandardEvents.cartLinesUpdate, handleCartUpdate);
    productForm.addEventListener(StandardEvents.cartError, handleCartError);
    document.addEventListener('cart:updated', handleBundlexCartUpdate);
    document.addEventListener('cart:refresh', handleBundlexCartUpdate);

    (async () => {
      for (const delay of [250, 500, 1000, 2000, 3000]) {
        await new Promise((resume) => setTimeout(resume, delay));
        if (settled) return;

        try {
          const cart = await fetchCartSnapshot();
          if (cartHasBundlexSelection(cart, selection)) {
            finish();
            return;
          }
        } catch {
          // A later cart event/check can still complete the handoff.
        }
      }
    })();
  });
}

/**
 * Adds the complete Bundlex selection, then asks EasySell to reuse the finished cart instead of adding
 * the main product again.
 * @param {NonNullable<ReturnType<typeof getBundlexHandoff>>} handoff
 */
async function openBundlexOfferInEasySell({ popupButton, productForm, addControl, selection }) {
  bundlexHandoffPending = true;
  popupButton.setAttribute('aria-busy', 'true');
  popupButton.setAttribute('aria-disabled', 'true');

  try {
    const cartAdd = waitForBundlexCartAdd(productForm, selection);
    addControl.click();
    await cartAdd;
    await syncFormFromCart();
    const modal = document.getElementById('easysell-modal');
    if (!modal || modal.getClientRects().length === 0) await window.openTysloForm?.(true);
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.warn('[easysell] Could not pass the Bundlex offer to the order form:', error);
    }
  } finally {
    bundlexHandoffPending = false;
    popupButton.removeAttribute('aria-busy');
    popupButton.removeAttribute('aria-disabled');
  }
}

/**
 * Writes only real changes, so the observer that triggers `render` settles.
 * @param {Element | null} element
 * @param {string} text
 */
function setText(element, text) {
  if (element && element.textContent !== text) element.textContent = text;
}

function scheduleRender() {
  if (!frame) frame = requestAnimationFrame(render);
}

function render() {
  frame = 0;

  const store = getStore();
  const lineTemplate = document.getElementById('easysell-line-editor');
  if (!store || !(lineTemplate instanceof HTMLTemplateElement)) return;

  const { items } = store.state.cart.cart;
  const rows = getRows();
  const canClose = getCloseControl() !== null;

  rows.forEach((row, index) => {
    const item = items[index];
    if (!item) return;

    if (!row.querySelector(EDITOR_SELECTOR)) row.append(lineTemplate.content.cloneNode(true));
    const editor = row.querySelector(EDITOR_SELECTOR);
    if (!editor) return;

    const edit = edits.get(index);
    const quantity = edit && edit.quantity > 0 ? edit.quantity : item.quantity;
    setText(editor.querySelector('[data-easysell-quantity]'), String(quantity));

    for (const button of editor.querySelectorAll('button')) {
      const action = button.dataset.easysellEdit;
      button.disabled = locked || (action === 'decrease' && quantity <= 1);
      if (action !== 'remove') continue;

      // The form can't show an empty order, so its last product can only go when the form can close.
      button.hidden = items.length === 1 && !canClose;
      button.setAttribute('aria-label', (button.dataset.label ?? '').replace('[title]', item.title ?? ''));
    }

    const error = editor.querySelector('.es-line-editor__error');
    if (error instanceof HTMLElement) {
      const message = failure?.index === index ? failure.message || error.dataset.fallback || '' : '';
      setText(error, message);
      error.hidden = message === '';
    }
  });

  const group = rows[0]?.closest('.es-group');
  const addTemplate = document.getElementById('easysell-add-product');
  let addButton = document.querySelector('#easysell .es-add-product');

  // Adding another product means closing the form to keep shopping, and the app must keep this order's products.
  if (!group || !canClose || store.getters['cart/onlySingleProduct'] === true) {
    addButton?.remove();
    return;
  }

  if (!addButton && addTemplate instanceof HTMLTemplateElement) {
    group.append(addTemplate.content.cloneNode(true));
    addButton = group.querySelector('.es-add-product');
  }
  if (addButton instanceof HTMLButtonElement) addButton.disabled = locked;
}

/**
 * @param {LineEdit} edit
 * @returns {Promise<{ ok: boolean, result: { item_count?: number, errors?: unknown, description?: string, message?: string } }>}
 */
async function changeCart(edit) {
  const body = JSON.stringify({ ...edit.target, quantity: edit.quantity });
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));

  return { ok: response.ok, result: await response.json() };
}

/**
 * Reloads the cart in the app's store, then repeats what the app does after its own remove button.
 * @param {EasySellStore} store
 */
async function refreshForm(store) {
  await store.dispatch('cart/fetchCart');
  store.dispatch('shipping/setShippingOptions', null);
  store.dispatch('bumps/initBumps', null);
  store.dispatch('calculator/calculateDiscount', null);
  window.ES_CHECK_ELIGIBILITY?.();
}

/**
 * Pulls external cart changes into EasySell without allowing overlapping store refreshes.
 * Bundlex adds several linked lines in one interaction and can emit more than one cart event.
 */
function syncFormFromCart() {
  if (formSync) {
    formSyncQueued = true;
    return formSync;
  }

  const store = getStore();
  if (!store) return Promise.resolve();

  formSync = (async () => {
    do {
      formSyncQueued = false;
      await refreshForm(store);
      render();
    } while (formSyncQueued);
  })()
    .catch(() => {
      // The form-open hook or the next cart event retries once EasySell is ready.
    })
    .finally(() => {
      formSync = null;
    });

  return formSync;
}

/**
 * Standard cart events fire before their requests settle. EasySell must wait for the final cart so a
 * Bundlex event cannot refresh the form with only part of the offer.
 * @param {Event} event
 */
async function handleExternalCartUpdate(event) {
  if ('promise' in event && event.promise instanceof Promise) {
    try {
      await event.promise;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[easysell] Cart update did not settle:', error);
      return;
    }
  }

  await syncFormFromCart();
}

/**
 * Keeps the product form's quantity in step with the cart, because the app sets this product's cart
 * quantity from it every time the form opens.
 * @param {EasySellStore} store
 * @param {number} variantId
 */
function syncProductFormQuantity(store, variantId) {
  const quantity = store.state.cart.cart.items.find((item) => item.variant_id === variantId)?.quantity;
  if (!quantity) return;

  for (const form of document.querySelectorAll('form[data-type="add-to-cart-form"]')) {
    if (!(form instanceof HTMLFormElement) || new FormData(form).get('id') !== String(variantId)) continue;

    const input = form.elements.namedItem('quantity');
    if (!(input instanceof HTMLInputElement)) continue;

    const selector = input.closest('quantity-selector-component');
    if (selector instanceof QuantitySelectorComponent) {
      selector.setValue(String(quantity));
      selector.updateButtonStates();
    } else {
      input.value = String(quantity);
    }
  }
}

/**
 * Puts focus back in the list after the focused line was removed.
 * @param {number} index - Where the removed line was.
 */
function restoreFocus(index) {
  if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) return;

  const rows = getRows();
  const row = rows[Math.min(index, rows.length - 1)];
  const target =
    row?.querySelector('[data-easysell-edit="remove"]:not([hidden])') ??
    document.querySelector('#easysell .es-add-product') ??
    document.getElementById('easysell');

  if (target instanceof HTMLElement) target.focus();
}

/** Sends the waiting edits one at a time, then lets the app catch up with the cart. */
async function flush() {
  clearTimeout(debounce);

  const store = getStore();
  if (flushing || !store) return;
  flushing = true;

  let emptied = false;
  let reloaded = true;
  /** @type {number | null} */
  let removedIndex = null;
  /** @type {Set<number>} Variants whose quantity was edited in this pass. */
  const edited = new Set();

  try {
    // Edits made while a pass is running are picked up by the next pass.
    while (!emptied && Array.from(edits.values()).some((edit) => !edit.sent)) {
      for (const [index, edit] of edits) {
        if (edit.sent) continue;
        edit.sent = true;

        try {
          const { ok, result } = await changeCart(edit);

          if (edit.quantity > 0) edited.add(edit.variantId);

          if (!ok) {
            // The cart explains itself, e.g. when it could only take what is in stock.
            const reason = typeof result.errors === 'string' ? result.errors : result.description || result.message;
            failure = { index, message: reason ?? '' };
          } else if (edit.quantity === 0) {
            removedIndex = index;
            emptied = result.item_count === 0;
          }
        } catch {
          // No usable answer from the cart; the list falls back to what the cart holds.
          failure = { index, message: '' };
        }
      }

      if (emptied) break;

      await refreshForm(store);
      for (const variantId of edited) syncProductFormQuantity(store, variantId);
      for (const [index, edit] of edits) {
        if (edit.sent) edits.delete(index);
      }
    }
  } catch {
    // The app couldn't reload the cart; its list catches up with the next edit or when the form reopens.
    reloaded = false;
  } finally {
    edits.clear();
    flushing = false;
    locked = false;
  }

  const button = heldButton;
  heldButton = null;
  button?.removeAttribute('aria-busy');

  if (emptied) {
    getCloseControl()?.click();
    return;
  }

  render();
  if (removedIndex !== null) restoreFocus(removedIndex);
  if (button?.isConnected && reloaded && !failure) button.click();
}

/** @param {MouseEvent} event */
function handleClick(event) {
  if (!(event.target instanceof Element)) return;

  const popupButton = event.target.closest(EASYSELL_POPUP_BUTTON_SELECTOR);
  if (bundlexHandoffPending && popupButton) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const handoff = getBundlexHandoff(event.target);
  if (handoff) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const missingProduct = handoff.activeOffer.querySelector('.bx-bundle-empty.js-bx-bundle-pool-trigger');
    if (missingProduct instanceof HTMLElement) {
      missingProduct.click();
      missingProduct.focus();
    } else {
      openBundlexOfferInEasySell(handoff);
    }
    return;
  }

  const button = event.target.closest(EASYSELL_BUTTON_SELECTOR);
  if (button instanceof HTMLButtonElement && formSync) {
    // An order must use the cart EasySell is currently loading, not its previous Vuex snapshot.
    event.preventDefault();
    event.stopImmediatePropagation();
    button.setAttribute('aria-busy', 'true');
    formSync.finally(() => {
      button.removeAttribute('aria-busy');
      if (button.isConnected) button.click();
    });
    return;
  }

  if (button instanceof HTMLButtonElement && (edits.size > 0 || flushing)) {
    // The app orders what its store holds, so the press waits until the store has the edits.
    event.preventDefault();
    event.stopImmediatePropagation();
    heldButton = button;
    button.setAttribute('aria-busy', 'true');
    flush();
    return;
  }

  const control = event.target.closest('#easysell [data-easysell-edit]');
  if (!(control instanceof HTMLButtonElement)) return;

  const action = control.dataset.easysellEdit;
  if (action === 'add') {
    getCloseControl()?.click();
    return;
  }

  const store = getStore();
  const row = control.closest('.es-line-item');
  if (!store || !(row instanceof HTMLElement) || locked) return;

  const { items } = store.state.cart.cart;
  const index = getRows().indexOf(row);
  const item = items[index];
  if (!item) return;

  const current = edits.get(index)?.quantity ?? item.quantity;
  const quantity = action === 'remove' ? 0 : Math.max(1, current + (action === 'increase' ? 1 : -1));
  if (quantity === current) return;

  // A variant id is ambiguous when the same variant sits on two lines, e.g. with different properties.
  const shared = items.filter((other) => other.variant_id === item.variant_id).length > 1;
  const target = shared ? { line: index + 1 } : { id: String(item.variant_id) };

  edits.set(index, { target, variantId: item.variant_id, quantity, sent: false });
  failure = null;
  clearTimeout(debounce);

  if (quantity === 0) {
    locked = true;
    flush();
  } else {
    debounce = setTimeout(flush, QUANTITY_DEBOUNCE);
  }

  render();

  // The decrease button disables itself at 1; keep keyboard focus inside the stepper.
  if (action === 'decrease' && control.disabled) {
    const increase = control.parentElement?.querySelector('[data-easysell-edit="increase"]');
    if (increase instanceof HTMLElement) increase.focus();
  }
}

const observer = new MutationObserver(scheduleRender);

/** Watches the form once the app has rendered it; the app redraws its lines whenever the cart or the fields change. */
function attach() {
  const form = document.getElementById('easysell');
  if (!form) return;

  if (form !== observedForm) {
    observer.disconnect();
    observer.observe(form, { childList: true, subtree: true, characterData: true });
    observedForm = form;
  }

  scheduleRender();
}

/**
 * Runs `attach` from one of the app's lifecycle hooks, keeping a hook that was defined before this module.
 * @param {'es_after_init_completed' | 'es_hook_open_popup'} name
 */
function attachOnAppHook(name, syncCart = false) {
  const previous = window[name];

  window[name] = () => {
    attach();
    previous?.();
    if (syncCart) syncFormFromCart();
  };
}

document.addEventListener('click', handleClick, { capture: true });
document.addEventListener(StandardEvents.cartLinesUpdate, handleExternalCartUpdate);
document.addEventListener('cart:updated', handleExternalCartUpdate);
document.addEventListener('cart:refresh', handleExternalCartUpdate);
attachOnAppHook('es_after_init_completed');
attachOnAppHook('es_hook_open_popup', true);
attach();
